import { useQuery, useQueryClient } from "@tanstack/react-query";
import { productsApi } from "../services/products";
import { type ProductListQuery } from "../types/products";

export function useProductCategories() {
  return useQuery({
    queryKey: ["products", "categories"],
    queryFn: productsApi.getCategories,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useHealthGoals() {
  return useQuery({
    queryKey: ["products", "health-goals"],
    queryFn: productsApi.getHealthGoals,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useProducts(params: Partial<ProductListQuery>) {
  return useQuery({
    queryKey: ["products", params],
    queryFn: () => productsApi.getProducts(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useProductsByCategory(
  categoryId: string,
  params?: { page?: number; perPage?: number }
) {
  return useQuery({
    queryKey: ["products", "category", categoryId, params],
    queryFn: () => productsApi.getProductsByCategory(categoryId, params),
    enabled: !!categoryId,
    placeholderData: (previousData) => previousData,
  });
}

export function useProductHome(params?: {
  productsPerCategory?: string;
  topProductsContext?: "onboarding" | "product-plan" | "user-product-plan";
  productPlanName?: string;
}) {
  return useQuery({
    queryKey: ["products", "home", params],
    queryFn: () => productsApi.getProductHome(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useProductDetails(productId: string) {
  return useQuery({
    queryKey: ["products", productId],
    queryFn: () => productsApi.getProductDetails(productId),
    enabled: !!productId,
    staleTime: 10 * 60_000, // 10 minutes
  });
}

export function usePrefetchProductDetails() {
  const queryClient = useQueryClient();

  return (productId: string) => {
    void queryClient.prefetchQuery({
      queryKey: ["products", productId],
      queryFn: () => productsApi.getProductDetails(productId),
    });
  };
}
